import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Briefcase, Globe, Shield } from "lucide-react";

// Constants
const CF_ATTRIBUTE_KEYS = {
  RAY: "cf.ray",
  COUNTRY: "cf.country",
  REGION: "cf.region",
  CITY: "cf.city",
  DATACENTER: "cf.datacenter",
  HTTP_PROTOCOL: "cf.http_protocol",
  TLS_VERSION: "cf.tls_version",
  ASN: "cf.asn",
  AS_ORGANIZATION: "cf.as_organization",
  BOT_SCORE: "cf.bot_score",
  VERIFIED_BOT: "cf.verified_bot",
  CONNECTING_IP: "cf.connecting_ip",
  USER_AGENT: "http.user_agent",
} as const;

const BOT_SCORE_THRESHOLDS = {
  HIGH: 80,
  MEDIUM: 50,
} as const;

const USER_AGENT_MAX_LENGTH = 100;
const IP_MASK_PARTS = 2;

// Types
interface CloudflareAttributes {
  cfRay?: string;
  cfCountry?: string;
  cfRegion?: string;
  cfCity?: string;
  cfDatacenter?: string;
  cfHttpProtocol?: string;
  cfTlsVersion?: string;
  cfAsn?: number;
  cfAsOrganization?: string;
  cfBotScore?: number;
  cfVerifiedBot?: boolean;
  cfConnectingIp?: string;
  userAgent?: string;
}

interface CloudflareInfoSectionProps {
  attributes: Record<string, unknown> | null;
}

// Helper functions (moved outside component)
const extractCloudflareAttributes = (
  attributes: Record<string, unknown> | null,
): CloudflareAttributes => {
  if (!attributes) {
    return {};
  }

  return {
    cfRay:
      typeof attributes[CF_ATTRIBUTE_KEYS.RAY] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.RAY] as string)
        : undefined,
    cfCountry:
      typeof attributes[CF_ATTRIBUTE_KEYS.COUNTRY] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.COUNTRY] as string)
        : undefined,
    cfRegion:
      typeof attributes[CF_ATTRIBUTE_KEYS.REGION] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.REGION] as string)
        : undefined,
    cfCity:
      typeof attributes[CF_ATTRIBUTE_KEYS.CITY] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.CITY] as string)
        : undefined,
    cfDatacenter:
      typeof attributes[CF_ATTRIBUTE_KEYS.DATACENTER] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.DATACENTER] as string)
        : undefined,
    cfHttpProtocol:
      typeof attributes[CF_ATTRIBUTE_KEYS.HTTP_PROTOCOL] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.HTTP_PROTOCOL] as string)
        : undefined,
    cfTlsVersion:
      typeof attributes[CF_ATTRIBUTE_KEYS.TLS_VERSION] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.TLS_VERSION] as string)
        : undefined,
    cfAsn:
      typeof attributes[CF_ATTRIBUTE_KEYS.ASN] === "number"
        ? (attributes[CF_ATTRIBUTE_KEYS.ASN] as number)
        : undefined,
    cfAsOrganization:
      typeof attributes[CF_ATTRIBUTE_KEYS.AS_ORGANIZATION] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.AS_ORGANIZATION] as string)
        : undefined,
    cfBotScore:
      typeof attributes[CF_ATTRIBUTE_KEYS.BOT_SCORE] === "number"
        ? (attributes[CF_ATTRIBUTE_KEYS.BOT_SCORE] as number)
        : undefined,
    cfVerifiedBot:
      typeof attributes[CF_ATTRIBUTE_KEYS.VERIFIED_BOT] === "boolean"
        ? (attributes[CF_ATTRIBUTE_KEYS.VERIFIED_BOT] as boolean)
        : undefined,
    cfConnectingIp:
      typeof attributes[CF_ATTRIBUTE_KEYS.CONNECTING_IP] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.CONNECTING_IP] as string)
        : undefined,
    userAgent:
      typeof attributes[CF_ATTRIBUTE_KEYS.USER_AGENT] === "string"
        ? (attributes[CF_ATTRIBUTE_KEYS.USER_AGENT] as string)
        : undefined,
  };
};

const getLocationString = (
  city?: string,
  region?: string,
  country?: string,
): string | null => {
  const parts = [city, region, country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

const getBotScoreVariant = (
  score: number,
): "destructive" | "secondary" | "outline" => {
  if (score >= BOT_SCORE_THRESHOLDS.HIGH) {
    return "destructive";
  }

  if (score >= BOT_SCORE_THRESHOLDS.MEDIUM) {
    return "secondary";
  }

  return "outline";
};

const maskIpAddress = (ip: string): string => {
  const parts = ip.split(".");
  return `${parts.slice(0, IP_MASK_PARTS).join(".")}.***`;
};

const truncateUserAgent = (userAgent: string): string => {
  return userAgent.length > USER_AGENT_MAX_LENGTH
    ? `${userAgent.slice(0, USER_AGENT_MAX_LENGTH)}...`
    : userAgent;
};

const hasCloudflareData = (attrs: CloudflareAttributes): boolean => {
  return !!(
    attrs.cfRay ||
    attrs.cfCountry ||
    attrs.cfDatacenter ||
    attrs.cfHttpProtocol
  );
};

// Sub-components for better organization
const CloudflareHeader = () => (
  <div className="flex items-center gap-2 text-sm border-b pb-2 mb-2">
    <span className="font-medium text-foreground">Request Context</span>
  </div>
);

const GeographicInfo = ({
  location,
  datacenter,
}: { location: string | null; datacenter?: string }) => {
  if (!location && !datacenter) {
    return null;
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-muted-foreground">
        <Globe className="h-3 w-3" />
        <span>Location:</span>
      </div>
      {location && <div className="font-mono text-foreground">{location}</div>}
      {datacenter && (
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-xs px-1 py-0">
            {datacenter}
          </Badge>
          <Tooltip>
            <TooltipTrigger>
              <span className="text-muted-foreground text-xs">datacenter</span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="max-w-64 text-xs">
                Cloudflare edge datacenter that processed this request
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

const NetworkInfo = ({
  httpProtocol,
  tlsVersion,
}: { httpProtocol?: string; tlsVersion?: string }) => {
  if (!httpProtocol && !tlsVersion) {
    return null;
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-muted-foreground">
        <Briefcase className="h-3 w-3" />
        <span>Transport:</span>
      </div>
      {httpProtocol && (
        <div className="font-mono text-foreground">{httpProtocol}</div>
      )}
      {tlsVersion && <div className="text-muted-foreground">{tlsVersion}</div>}
    </div>
  );
};

const SecurityInfo = ({
  botScore,
  verifiedBot,
}: { botScore?: number; verifiedBot?: boolean }) => {
  if (botScore === undefined && verifiedBot === undefined) {
    return null;
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-muted-foreground">
        <Shield className="h-3 w-3" />
        <span>Security:</span>
      </div>
      {botScore !== undefined && (
        <div className="flex items-center gap-2">
          <Badge variant={getBotScoreVariant(botScore)} className="text-xs">
            Bot Score: {botScore}
          </Badge>
          <Tooltip>
            <TooltipTrigger>
              <span className="text-muted-foreground text-xs">?</span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="max-w-64 text-xs">
                Bot detection score (1-99). Higher scores indicate more bot-like
                behavior.
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
      {verifiedBot && (
        <Badge variant="secondary" className="text-xs">
          Verified Bot
        </Badge>
      )}
    </div>
  );
};

const RayAndIspInfo = ({
  cfRay,
  asOrganization,
  asn,
}: { cfRay?: string; asOrganization?: string; asn?: number }) => {
  if (!cfRay && !asOrganization) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
      {cfRay && (
        <div>
          <span className="text-muted-foreground">CF-Ray:</span>
          <div className="font-mono text-foreground">{cfRay}</div>
        </div>
      )}
      {asOrganization && (
        <div>
          <span className="text-muted-foreground">ISP:</span>
          <div className="font-mono text-foreground">
            {asOrganization}
            {asn && <span className="text-muted-foreground"> (AS{asn})</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const ClientInfo = ({
  connectingIp,
  userAgent,
}: { connectingIp?: string; userAgent?: string }) => {
  if (!connectingIp && !userAgent) {
    return null;
  }

  return (
    <>
      {connectingIp && (
        <div className="text-xs">
          <span className="text-muted-foreground">Client IP:</span>
          <div className="font-mono text-foreground">
            {maskIpAddress(connectingIp)}
          </div>
        </div>
      )}
      {userAgent && (
        <div className="text-xs">
          <span className="text-muted-foreground">User Agent:</span>
          <div className="font-mono text-foreground text-xs break-all max-h-8 overflow-hidden">
            {truncateUserAgent(userAgent)}
          </div>
        </div>
      )}
    </>
  );
};

export function CloudflareInfoSection({
  attributes,
}: CloudflareInfoSectionProps) {
  const cfAttrs = extractCloudflareAttributes(attributes);

  if (!hasCloudflareData(cfAttrs)) {
    return null;
  }

  const locationString = getLocationString(
    cfAttrs.cfCity,
    cfAttrs.cfRegion,
    cfAttrs.cfCountry,
  );

  return (
    <div className="space-y-3">
      <CloudflareHeader />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
        <GeographicInfo
          location={locationString}
          datacenter={cfAttrs.cfDatacenter}
        />
        <NetworkInfo
          httpProtocol={cfAttrs.cfHttpProtocol}
          tlsVersion={cfAttrs.cfTlsVersion}
        />
        <SecurityInfo
          botScore={cfAttrs.cfBotScore}
          verifiedBot={cfAttrs.cfVerifiedBot}
        />
      </div>

      <RayAndIspInfo
        cfRay={cfAttrs.cfRay}
        asOrganization={cfAttrs.cfAsOrganization}
        asn={cfAttrs.cfAsn}
      />

      <ClientInfo
        connectingIp={cfAttrs.cfConnectingIp}
        userAgent={cfAttrs.userAgent}
      />
    </div>
  );
}
